/**
 * Satisfy the design system's `window.lucide` contract from a bundled dependency.
 *
 * `src/design/components/icon/Icon.jsx` reads its glyphs from `window.lucide.icons` and
 * renders an empty box of the right size when that global is absent:
 *
 * > Real Lucide glyphs, read from the Lucide UMD build on the page (window.lucide.icons).
 * > […] If Lucide has not loaded the component renders an empty box of the right size so
 * > layout is unaffected.
 *
 * That is the right shape for the reference screens, which load the UMD build from unpkg
 * with a script tag. It is the wrong shape here, and the failure is quiet: **no icon
 * anywhere, and no error**, because the component's fallback is deliberately silent about
 * layout. So this module is imported for its side effect before React renders, and
 * `assertLucideLoaded` turns the quiet version into a loud one.
 *
 * The vendored file is not edited to import `lucide-react` instead — `web/src/design/` is
 * checksummed and an edit there is lost on the next pull (`VENDOR.md`). Assigning the
 * global it already looks for satisfies the existing contract rather than changing it.
 */

import { icons } from 'lucide';

declare global {
  interface Window {
    lucide?: { icons?: Record<string, unknown> } | Record<string, unknown>;
  }
}

export function installLucide(): void {
  window.lucide = { icons };
}

export function assertLucideLoaded(): void {
  const lib = window.lucide as { icons?: Record<string, unknown> } | undefined;
  const available = lib?.icons ? Object.keys(lib.icons).length : 0;
  if (available === 0) {
    // Thrown rather than logged. An operator UI with no icons is a UI whose kill switch
    // is an empty 16px box, and `Icon.jsx` will not say so on its own.
    throw new Error(
      'Lucide did not load: window.lucide.icons is empty, so every Icon renders blank. ' +
        'Check that `lucide` is installed and that installLucide() runs before render.',
    );
  }
}
